﻿import destr from "destr"
import type { Pinia } from 'pinia'

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>;

interface ShardViaConfig {
    sourceStoreId: string		// source store id
    sourceDataField?: string 	// source data field, default data
    localFk: string				// foreign key (in which property in local table is source table id)
    sourcePk?: string			// primary key in source store - default object key
    sourceValueField: string	// in which field is shard stored in source table
}

interface EntityShardConfig {
    matchStoreId: string;	// how to distingush store
    shardKeyOf: ( record: any ) => string | number | null;	// how to build a shard (record) => (key from record)
    keyPrefix: string;		// any entity prefix
    dataField?: string;		// in which field are data stored (in Pinia it is data)

    migration?: {			// how migration is done through shardVia
        shardVia?: ShardViaConfig
    }
}

interface ShardedPersistOptions {
    storage?: StorageLike;
    indexKey?: string;		// complex shard index
    entities: EntityShardConfig[];
    debounceMs?: number;
}

function lsKey ( prefix: string, shard: string | number ) {
    const shardKey = String( shard )

    return `${ prefix }[${ shardKey }]`
}

function readJSON<T> ( storage: StorageLike, key: string ): T | null {
    const raw = storage.getItem( key )

    if ( !raw )
        return null

    try {
        return destr( raw ) as T
    } catch {
        return null
    }
}

function writeJSON ( storage: StorageLike, key: string, value: unknown ) {
    storage.setItem( key, JSON.stringify( value ) )
}

// create Map<pk, shardValue> for legacy source store
function buildLookupFromLegacyStore (
    sourceLegacy: any,
    dataField = 'data',
    sourcePk = 'id',
    sourceValueField: string
): Map<string | number, string | number> {
    const out = new Map<string | number, string | number>()

    const data = sourceLegacy?.[ dataField ] as Record<string, any> | undefined

    if ( !data )
        return out

    for ( const [ objKey, rec ] of Object.entries<any>( data ) ) {
        const pk = rec?.[ sourcePk ] ?? objKey
        const shardVal = rec?.[ sourceValueField ]

        if ( pk != null && shardVal != null )
            out.set( pk, shardVal )
    }

    return out
}

function buildLookupFromShardedStorage (
    storage: StorageLike,
    index: { [ entityPrefix: string ]: Array<string | number> },
    sourcePrefix: string,
    sourcePk: string,
    sourceValueField: string
): Map<string | number, string | number> {
    const out = new Map<string | number, string | number>()
    const shards = index[ sourcePrefix ] ?? []

    for ( const shard of shards ) {
        const shardKey = lsKey( sourcePrefix, shard )
        const part = readJSON<Record<string, any>>( storage, shardKey ) ?? {}

        for ( const [ objKey, rec ] of Object.entries<any>( part ) ) {
            const pk = rec?.[ sourcePk ] ?? objKey
            const shardVal = rec?.[ sourceValueField ]

            if ( pk != null && shardVal != null )
                out.set( pk, shardVal )
        }
    }

    return out
}

export function installShardedPersist ( pinia: Pinia, opts: ShardedPersistOptions ) {
    const storage = opts.storage ?? window.localStorage
    const indexKey = opts.indexKey ?? 'orm:index'
    const debounceMs = opts.debounceMs ?? 250

    type ShardIndex = {
        [ entityPrefix: string ]: Array<string | number>;
    };

    const writeIndex = ( idx: ShardIndex ) => writeJSON( storage, indexKey, idx )

    function rebuildIndex ( storage: StorageLike, entities: EntityShardConfig[] ): ShardIndex {
        const index: ShardIndex = {}

        // for each entity config
        for ( const entity of entities ) {
            const prefix = entity.keyPrefix
            index[ prefix ] = []

            // Scan through all storage keys
            for ( let i = 0; i < storage.length; i++ ) {
                const key = storage.key( i )

                if ( !key )
                    continue

                // check if key matches our shard pattern (prefix[something])
                const match = key.match( new RegExp( `^${prefix}\\[(.+)]$` ) )

                // add the shard key to the index if matches
                if ( match && match[ 1 ] && !index[ prefix ].includes( match[ 1 ] ) )
                    index[ prefix ].push( match[ 1 ] )
            }
        }

        return index
    }

    const readIndex = (): ShardIndex => {
        const existingIndex = readJSON<ShardIndex>( storage, indexKey )

        if ( existingIndex )
            return existingIndex

        // if index is missing or invalid, rebuild it
        const rebuiltIndex = rebuildIndex( storage, opts.entities )

        writeIndex( rebuiltIndex )

        return rebuiltIndex
    }

    // migrate from monolitical key to sharded one
    function migrateLegacyIfNeeded () {
        const idx = readIndex()

        let migrated = false

        for ( const entity of opts.entities ) {
            const legacy = readJSON<any>( storage, entity.matchStoreId )

            if ( legacy === null )
                continue

            const dataField = entity.dataField ?? 'data'

            if ( !legacy[ dataField ] )
                continue

            const data = legacy[ dataField ] as Record<string, any> | undefined

            if ( !data )
                continue

            let viaLookup: Map<string | number, string | number> | undefined

            const via = entity.migration?.shardVia

            if ( via ) {
                const sourceLegacy = readJSON<any>( storage, via.sourceStoreId )

                if ( sourceLegacy ) {
                    viaLookup = buildLookupFromLegacyStore(
                        sourceLegacy,
                        via.sourceDataField ?? 'data',
                        via.sourcePk ?? 'id',
                        via.sourceValueField
                    )
                } else {
                    const srcCfg = opts.entities.find( e => e.matchStoreId === via.sourceStoreId )

                    if ( srcCfg ) {
                        viaLookup = buildLookupFromShardedStorage(
                            storage,
                            idx,
                            srcCfg.keyPrefix,
                            via.sourcePk ?? 'id',
                            via.sourceValueField
                        )
                    } else {
                        viaLookup = new Map()
                    }
                }
            }

            for ( const [ id, rec ] of Object.entries<any>( data ) ) {
                let shard: string | number | null = null

                if ( via && viaLookup ) {
                    const fkVal = rec?.[ via.localFk ]

                    if ( fkVal != null )
                        shard = viaLookup.get( fkVal ) ?? null

                    // create new direct field during migration
                    if ( shard !== null && ( rec[ via.sourceValueField ] === undefined || rec[ via.sourceValueField ] === null ) )
                        rec[ via.sourceValueField ] = shard
                }

                if ( shard == null )
                    shard = entity.shardKeyOf( rec )

                if ( shard == null )
                    continue

                const prefix = entity.keyPrefix
                const shardKey = lsKey( prefix, shard )

                const shardData = readJSON<Record<string, any>>( storage, shardKey ) ?? {}

                shardData[ id ] = rec

                writeJSON( storage, shardKey, shardData )

                // and store to index
                if ( !idx[ prefix ] )
                    idx[ prefix ] = []

                const shardKeyNorm = String( shard )

                if ( !idx[ prefix ].includes( shardKeyNorm ) )
                    idx[ prefix ].push( shardKeyNorm )
            }

            storage.removeItem( entity.matchStoreId )

            migrated = true
        }

        if ( migrated )
            writeIndex( idx )
    }

    migrateLegacyIfNeeded()

    // Debounce per store
    const timers = new Map<string, any>()

    // for every new store we need sharded routines
    pinia.use( ( { store } ) => {
        const cfg = opts.entities.find( e => store.$id === e.matchStoreId )

        if ( !cfg )
            return

        const dataField = cfg.dataField ?? 'data'

        // we need to load all fragments and complete it in RAM, we can do lazy-load here if necessary
        const idx = readIndex()
        const shards = idx[ cfg.keyPrefix ] ?? []

        let merged: Record<string, any> = {}

        for ( const shard of shards ) {
            const shardKey = lsKey( cfg.keyPrefix, shard )

            const part = readJSON<Record<string, any>>( storage, shardKey )

            if ( part )
                merged = { ...merged, ...part }
        }

        // we have to combine everything in store store without trigger reactivity
        if ( store.$state && ( store.$state as any )[ dataField ] && Object.keys( merged ).length ) {
            store.$patch( ( state: any ) => {
                state[ dataField ] = { ...( state[ dataField ] ?? {} ), ...merged }
            } )
        }

        // if anything is changed in store we can write only to selected shard
        store.$subscribe( ( mutation, state ) => {
            clearTimeout( timers.get( store.$id ) )

            const t = setTimeout( () => {
                try {
                    const records: Record<string, any> = ( state as any )[ dataField ] ?? {}
                    const byShard = new Map<string | number, Record<string, any>>()

                    for ( const [ id, rec ] of Object.entries( records ) ) {
                        const shard = cfg.shardKeyOf( rec )

                        if ( shard == null )
                            continue

                        if ( !byShard.has( shard ) )
                            byShard.set( shard, {} )

                        byShard.get( shard )![ id ] = rec
                    }

                    const idxNow = readIndex()

                    // identify what to remove from index and storage
                    const prefix = cfg.keyPrefix
                    const existingShards = idxNow[ prefix ] || []

                    const currentShards = new Set(
                        Object.values( records )
                            .map( record => cfg.shardKeyOf( record ) )
                            .filter( key => key !== null )
                            .map( String )
                    )

                    const shardsToDelete = existingShards.filter( shard => !currentShards.has( shard ) )

                    shardsToDelete.forEach( shardKey => {
                        const fullShardKey = lsKey( prefix, shardKey )

                        storage.removeItem( fullShardKey )
                    } )

                    if ( shardsToDelete.length > 0 ) {
                        idxNow[ prefix ] = existingShards.filter( shard => currentShards.has( shard ) )
                        if ( idxNow[ prefix ].length === 0 ) {
                            delete idxNow[ prefix ]
                        }
                    }

                    // and write every shard
                    for ( const [ shard, payload ] of byShard.entries() ) {
                        const shardKey = lsKey( cfg.keyPrefix, shard )

                        writeJSON( storage, shardKey, payload )

                        if ( !idxNow[ cfg.keyPrefix ] )
                            idxNow[ cfg.keyPrefix ] = []

                        const shardKeyNorm = String( shard )

                        if ( !idxNow[ cfg.keyPrefix ].includes( shardKeyNorm ) )
                            idxNow[ cfg.keyPrefix ].push( shardKeyNorm )
                    }

                    writeIndex( idxNow )
                } catch ( e ) {
                    // we can log any error here
                }
            }, debounceMs )

            timers.set( store.$id, t )
        } )
    } )
}
